import { PaymentMethodMutationResponse } from '@sola/schema/dotcom/proxy-api/paymentMethods.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import type { SupporterPaymentMethodDetails } from '@sola/schema/dotcom/supporters/paymentMethods.js';
import type { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import type { UserError } from '@sola/shared-utils/errors-user.js';
import { userErrorBadParam } from '@sola/shared-utils/errors/misc.js';
import {
  userErrorPaymentMethodCreate,
  userErrorPaymentMethodDelete,
  userErrorPaymentMethodUpdate,
} from '@sola/shared-utils/errors/supporters.js';
import {
  httpDeleteWithStatus,
  httpPostWithStatus,
  httpPutWithStatus,
  isErrorResponse,
  isUserError,
} from '@sola/shared-utils/httpClient.js';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.js';

import { MY_API_BASE } from '../constants';

export type GraphQLPaymentMethod = GraphQLSupporterPaymentMethod;
export type PaymentMethodMutationError = UserError | UserError[];

const userErrorsToDisplay: UserError['type'][] = [
  SupporterErrorTypeEnum.PaymentMethodCreate,
  SupporterErrorTypeEnum.PaymentMethodDelete,
  SupporterErrorTypeEnum.PaymentMethodUpdate,
  SupporterErrorTypeEnum.PaymentMethodAlreadyExists,
];

const getResponseUserErrors = (
  response?: ErrorResponse | PaymentMethodMutationResponse,
): UserError[] | undefined => {
  if (
    response &&
    typeof response === 'object' &&
    'errors' in response &&
    Array.isArray(response.errors)
  ) {
    const userErrors = response.errors.filter(isUserError);
    if (userErrors.length) {
      return userErrors;
    }
  }
};

const getDisplayableUserErrors = (
  error: unknown,
): PaymentMethodMutationError | undefined => {
  if (isUserError(error) && userErrorsToDisplay.includes(error.type)) {
    return error;
  }

  if (Array.isArray(error)) {
    const displayableErrors = error.filter(
      (entry): entry is UserError =>
        isUserError(entry) && userErrorsToDisplay.includes(entry.type),
    );
    if (displayableErrors.length) {
      return displayableErrors;
    }
  }
};

export const routePaymentMethod = (
  supporterId: string,
  paymentMethodId?: string,
) =>
  `${MY_API_BASE}/supporters/${supporterId}/payment-methods/${paymentMethodId ? `${paymentMethodId}/` : ''}`;

export const removeSupporterPaymentMethod = async (
  supporterId?: string,
  paymentMethodId?: string,
  newPaymentMethodId?: string,
  // TODO: pull in PaymentMethodMutationResponse return type
): Promise<PaymentMethodMutationResponse> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  if (!paymentMethodId) {
    throw userErrorBadParam('paymentMethodId');
  }

  const { response, statusCode } = await httpDeleteWithStatus<
    PaymentMethodMutationResponse | ErrorResponse
  >(
    `${routePaymentMethod(supporterId, paymentMethodId)}${newPaymentMethodId ? `?newPaymentMethodId=${newPaymentMethodId}` : ''}`,
    {
      signRequest: true,
    },
  );

  if (
    ![200, 204, 207].includes(statusCode) ||
    !response ||
    isErrorResponse(response)
  ) {
    throw userErrorPaymentMethodDelete(statusCode);
  }

  return response;
};

/**
 * Add a new payment method.
 */
export const addSupporterPaymentMethod = async ({
  globalId,
  paymentMethodDetails,
  commitmentIds,
}: {
  globalId: string;
  paymentMethodDetails: SupporterPaymentMethodDetails;
  commitmentIds?: string[];
}): Promise<PaymentMethodMutationResponse | PaymentMethodMutationError> => {
  try {
    // Catch and return generic message.
    if (!globalId || !paymentMethodDetails) {
      throw Error;
    }

    const { response, statusCode } = await httpPostWithStatus<
      PaymentMethodMutationResponse | ErrorResponse
    >(
      routePaymentMethod(globalId),
      {
        paymentMethodDetails,
        ...(commitmentIds && { commitmentIds }),
      },
      {
        signRequest: true,
      },
    );

    // Added paymentment, but has commitment errors.
    // Seems like this should be a utility driven by shared proxy-api logic
    // if (statusCode === 207) {
    //   // TODO waiting on UX for messaging.
    // }
    // Something went wrong with adding the payment method.
    if (![201, 207].includes(statusCode)) {
      throw (
        getResponseUserErrors(response) ??
        userErrorPaymentMethodCreate(statusCode)
      );
    }

    return response as PaymentMethodMutationResponse;
  } catch (error) {
    return getDisplayableUserErrors(error) ?? userErrorPaymentMethodCreate();
  }
};

/**
 * Update a payment method.
 */
export const updateSupporterPaymentMethod = async ({
  globalId,
  paymentMethod,
  commitmentIds,
}: {
  globalId: string;
  paymentMethod: GraphQLPaymentMethod;
  commitmentIds?: string[];
}): Promise<PaymentMethodMutationResponse | PaymentMethodMutationError> => {
  try {
    // Catch and return generic message.
    if (!globalId || !paymentMethod?.id) {
      throw Error;
    }

    const { response, statusCode } = await httpPutWithStatus<
      PaymentMethodMutationResponse | ErrorResponse
    >(
      routePaymentMethod(globalId, paymentMethod.id),
      {
        paymentMethod,
        ...(commitmentIds && { commitmentIds }),
      },
      {
        signRequest: true,
      },
    );

    // Updated payment method, but has commitment errors.
    // Seems like this should be a utility driven by shared proxy-api logic
    if (statusCode === 207) {
      // TODO waiting on UX for messaging.
    }
    // Something went wrong with updating the payment method.
    else if (statusCode !== 200) {
      throw (
        getResponseUserErrors(response) ??
        userErrorPaymentMethodUpdate(statusCode)
      );
    }

    return response as PaymentMethodMutationResponse;
  } catch (error) {
    return getDisplayableUserErrors(error) ?? userErrorPaymentMethodUpdate();
  }
};
